import { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { useAuth } from "../App";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QuotaContext = createContext(null);

export const useQuota = () => {
  const ctx = useContext(QuotaContext);
  if (!ctx) throw new Error("useQuota must be used within QuotaProvider");
  return ctx;
};

export const QuotaProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchQuota = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/quota`);
      setQuota(res.data);
    } catch (err) {
      console.error("Failed to fetch quota:", err.message);
      setQuota(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  const isAdmin = quota?.is_admin || user?.is_admin;
  const unlimited = quota?.unlimited || isAdmin;

  const remaining = useCallback((type) => {
    if (unlimited) return Infinity;
    const bucket = quota?.quotas?.[type];
    if (!bucket || typeof bucket !== "object") return 0;
    return Math.max(0, (bucket.limit || 0) - (bucket.used || 0));
  }, [unlimited, quota]);

  const used = useCallback((type) => {
    if (unlimited) return 0;
    return quota?.quotas?.[type]?.used ?? 0;
  }, [unlimited, quota]);

  const limit = useCallback((type) => {
    if (unlimited) return Infinity;
    return quota?.quotas?.[type]?.limit ?? 0;
  }, [unlimited, quota]);

  const canUse = useCallback((type) => remaining(type) > 0, [remaining]);

  const contextValue = useMemo(() => ({
    quota, loading, fetchQuota, isAdmin, unlimited, remaining, used, limit, canUse
  }), [quota, loading, fetchQuota, isAdmin, unlimited, remaining, used, limit, canUse]);

  return (
    <QuotaContext.Provider value={contextValue}>
      {children}
    </QuotaContext.Provider>
  );
};
